import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { listDashboardConfigs } from '../api/dashboardConfig.api';

export default function AppGalleryPage({ darkMode }) {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await listDashboardConfigs();
      if (!mounted) return;
      if (res.error) setError(res.error);
      else setApps(res.data ?? []);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  const publishUrl = (id) => `${window.location.origin}/p/${id}`;

  const copyUrl = (id) => {
    const url = publishUrl(id);
    navigator.clipboard?.writeText(url).then(() => {
      // Could add a snackbar/toast here
    });
  };

  const filteredApps = apps.filter(
    (a) =>
      !searchQuery ||
      (a.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (a.description || '').toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DashboardIcon />
          <Typography variant="h5">Apps</Typography>
        </Box>
        <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate('/dashboard-builder')}>
          Manage Apps
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Run your apps using the published URL. Click <strong>Run</strong> to open in full view, or copy the URL to share or embed.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        placeholder="Search apps..."
        size="small"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3, maxWidth: 320 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredApps.length === 0 ? (
        <Alert severity="info">
          {searchQuery ? 'No apps match your search.' : 'No apps yet. Create one in App Builder.'}
          <Button sx={{ ml: 1 }} size="small" onClick={() => navigate('/dashboard-builder')}>
            Go to App Builder
          </Button>
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {filteredApps.map((app) => {
            const id = app._id || app.id;
            const url = publishUrl(id);
            return (
              <Grid item xs={12} sm={6} md={4} key={id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 3 },
                  }}
                >
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {app.name || 'Untitled App'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {app.description || 'No description'}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2, pt: 0, flexWrap: 'wrap', gap: 0.5 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                    >
                      Run
                    </Button>
                    <Tooltip title="Copy published URL">
                      <IconButton size="small" onClick={() => copyUrl(id)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => navigate(`/dashboard-builder/edit/${id}`)}>
                      Edit
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
